import React from 'react';
import { ArrowDown, ArrowUp, Clock } from 'lucide-react';

const Change = ({ before, after, invert, fmt = (v) => v, testId }) => {
  if (before == null || after == null) return <span className="text-gray-400">—</span>;
  const diff = after - before;
  const good = invert ? diff < 0 : diff > 0;
  const Icon = diff > 0 ? ArrowUp : ArrowDown;
  return (
    <span className="inline-flex items-center gap-1" data-testid={testId}>
      {fmt(before)} → <b>{fmt(after)}</b>
      {diff !== 0 && <span className={`inline-flex items-center text-xs font-semibold ${good ? 'text-green-700' : 'text-rose-700'}`}><Icon className="w-3 h-3" />{fmt(Math.abs(Math.round(diff * 10) / 10))}</span>}
    </span>
  );
};

const Row = ({ t, i }) => {
  const b = t.baseline?.query || {};
  const a = t.after?.query;
  const bp = t.baseline?.page || {};
  const ap = t.after?.page;
  return (
    <tr className="border-b border-gray-100 align-top" data-testid={`fix-track-row-${i}`}>
      <td className="px-3 py-3">
        <p className="font-semibold text-gray-900">{t.query}</p>
        <p className="text-xs text-gray-500 break-all">{t.page.replace('https://audioandco.com', '') || '/'}</p>
        <p className="text-xs text-gray-500 mt-0.5">{t.kind === 'blog' ? 'Applied' : 'Built'} {t.applied_at.slice(0, 10)}</p>
      </td>
      {t.status === 'complete' ? (
        <>
          <td className="px-3 py-3 text-sm"><Change before={b.position} after={a.position} invert testId={`fix-track-${i}-position`} /></td>
          <td className="px-3 py-3 text-sm"><Change before={b.clicks} after={a.clicks} testId={`fix-track-${i}-clicks`} /></td>
          <td className="px-3 py-3 text-sm"><Change before={b.impressions} after={a.impressions} /></td>
          <td className="px-3 py-3 text-sm"><Change before={bp.clicks} after={ap.clicks} /></td>
        </>
      ) : (
        <>
          <td className="px-3 py-3 text-sm">{b.position ?? '—'}</td>
          <td className="px-3 py-3 text-sm">{b.clicks ?? '—'}</td>
          <td className="px-3 py-3 text-sm">{b.impressions ?? '—'}</td>
          <td className="px-3 py-3 text-sm">{bp.clicks ?? '—'}</td>
        </>
      )}
      <td className="px-3 py-3 text-sm">
        {t.status === 'complete'
          ? <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">Results in</span>
          : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700" data-testid={`fix-track-${i}-days-left`}><Clock className="w-3 h-3" />{t.days_left} days left</span>}
      </td>
    </tr>
  );
};

export const FixTrackingCard = ({ items }) => (
  <div className="bg-white rounded-lg shadow p-5" data-testid="fix-tracking">
    <h3 className="text-lg font-bold text-gray-900">Before and after</h3>
    <p className="text-sm text-gray-600">Each fix is compared over the 28 days before it went live and the 28 days after. Results appear about a month after a fix goes live, once Google&rsquo;s data has caught up.</p>
    {!items.length ? (
      <p className="text-sm text-gray-500 mt-4" data-testid="fix-tracking-empty">No fixes tracked yet. Apply a blog fix, or press &ldquo;Mark as built&rdquo; on a main page fix once it&rsquo;s live.</p>
    ) : (
      <div className="overflow-x-auto mt-4">
        <table className="w-full min-w-[760px] text-left">
          <thead className="text-xs uppercase text-gray-500 border-b border-gray-200">
            <tr><th className="px-3 py-2">Fix</th><th className="px-3 py-2">Position</th><th className="px-3 py-2">Clicks (search)</th><th className="px-3 py-2">Impressions</th><th className="px-3 py-2">Page clicks (all)</th><th className="px-3 py-2">Status</th></tr>
          </thead>
          <tbody>{items.map((t, i) => <Row key={t.id} t={t} i={i} />)}</tbody>
        </table>
      </div>
    )}
  </div>
);

export default FixTrackingCard;
